<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import axiosInstance from '@/plugins/axios'

const toast = useToast()
const loading = ref(false)
const testing = ref(false)
const testResult = ref<'success' | 'error' | null>(null)
const testMessage = ref('')

const settings = ref({
  host: '',
  port: 587,
  secure: false,
  user: '',
  pass: '',
  from: ''
})

const isSaveDisabled = computed(() => !settings.value.host || !settings.value.user || !settings.value.from)
const isTestDisabled = computed(() => !settings.value.host)

onMounted(loadSettings)

async function loadSettings() {
  loading.value = true
  try {
    const { data } = await axiosInstance.get('/settings/mail')
    if (data) {
      settings.value = {
        host: data.host || '',
        port: data.port || 587,
        secure: data.secure || false,
        user: data.user || '',
        pass: data.pass || '',
        from: data.from || ''
      }
    }
  } catch {
    toast.add({ severity: 'error', summary: 'Fehler', detail: 'Mail-Einstellungen konnten nicht geladen werden.', life: 3000 })
  } finally {
    loading.value = false
  }
}

async function saveSettings() {
  loading.value = true
  try {
    await axiosInstance.post('/settings/mail', settings.value)
    toast.add({ severity: 'success', summary: 'Gespeichert', detail: 'Mail-Einstellungen wurden erfolgreich gespeichert.', life: 3000 })
  } catch {
    toast.add({ severity: 'error', summary: 'Fehler', detail: 'Mail-Einstellungen konnten nicht gespeichert werden.', life: 3000 })
  } finally {
    loading.value = false
  }
}

async function testConnection() {
  testing.value = true
  testResult.value = null
  testMessage.value = ''
  try {
    const { data } = await axiosInstance.post('/settings/mail/test', settings.value)
    testResult.value = 'success'
    testMessage.value = data.message || 'Verbindung erfolgreich.'
  } catch (err: any) {
    testResult.value = 'error'
    testMessage.value = err.response?.data?.message || 'Verbindung fehlgeschlagen.'
  } finally {
    testing.value = false
  }
}

function fillMailpit() {
  settings.value = {
    host: 'localhost',
    port: 1025,
    secure: false,
    user: 'test',
    pass: 'test',
    from: 'noreply@localhost'
  }
  testResult.value = null
}
</script>

<template>
  <div class="mail-settings-container">

    <Card class="mb-4">
      <template #title>
        <i class="pi pi-envelope mr-2" />
        SMTP-Konfiguration
      </template>
      <template #content>
        <div class="formgrid grid">

          <div class="field col-12 md:col-8">
            <label for="host" class="font-medium block mb-2">SMTP Host <span class="text-red-500">*</span></label>
            <InputText id="host" v-model="settings.host" placeholder="smtp.example.com" class="w-full" :disabled="loading" />
            <small class="text-color-secondary">Hostname oder IP-Adresse des SMTP-Servers</small>
          </div>

          <div class="field col-12 md:col-4">
            <label for="port" class="font-medium block mb-2">Port</label>
            <InputNumber id="port" v-model="settings.port" :useGrouping="false" placeholder="587" class="w-full" :disabled="loading" />
          </div>

          <div class="field col-12 md:col-6">
            <label for="user" class="font-medium block mb-2">Benutzername <span class="text-red-500">*</span></label>
            <InputText id="user" v-model="settings.user" placeholder="user@example.com" class="w-full" autocomplete="off" :disabled="loading" />
          </div>

          <div class="field col-12 md:col-6">
            <label for="pass" class="font-medium block mb-2">Passwort</label>
            <Password id="pass" v-model="settings.pass" :feedback="false" toggleMask fluid :disabled="loading" />
          </div>

          <div class="field col-12 md:col-8">
            <label for="from" class="font-medium block mb-2">Absender-Adresse <span class="text-red-500">*</span></label>
            <InputText id="from" v-model="settings.from" placeholder="noreply@example.com" class="w-full" :disabled="loading" />
            <small class="text-color-secondary">Erscheint im "Von"-Feld aller ausgehenden E-Mails</small>
          </div>

          <div class="field col-12 md:col-4 flex align-items-end pb-2">
            <div class="flex align-items-center gap-2">
              <Checkbox inputId="secure" v-model="settings.secure" :binary="true" :disabled="loading" />
              <label for="secure" class="font-medium" style="cursor: pointer;">SSL / TLS</label>
            </div>
          </div>

        </div>
      </template>
      <template #footer>
        <div class="flex align-items-center gap-2">
          <Button
            label="Mailpit (lokal)"
            icon="pi pi-bolt"
            severity="secondary"
            size="small"
            text
            @click="fillMailpit"
          />
          <span class="text-color-secondary" style="font-size: 0.78rem;">localhost:1025, kein Auth, kein SSL</span>
        </div>
      </template>
    </Card>

    <Card>
      <template #title>
        <i class="pi pi-check-circle mr-2" />
        Verbindung testen & Speichern
      </template>
      <template #content>
        <p class="text-color-secondary mt-0 mb-3">
          Testen Sie die Verbindung zum SMTP-Server bevor Sie speichern.
        </p>
        <div class="flex align-items-center gap-3 flex-wrap">
          <Button
            label="Verbindung testen"
            icon="pi pi-wifi"
            severity="secondary"
            outlined
            :loading="testing"
            :disabled="isTestDisabled || loading"
            @click="testConnection"
          />
          <Transition name="fade">
            <div v-if="testResult" class="test-banner" :class="testResult">
              <i :class="testResult === 'success' ? 'pi pi-check-circle' : 'pi pi-times-circle'" />
              <span>{{ testMessage }}</span>
            </div>
          </Transition>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-content-end">
          <Button
            label="Einstellungen speichern"
            icon="pi pi-save"
            :loading="loading"
            :disabled="isSaveDisabled"
            @click="saveSettings"
          />
        </div>
      </template>
    </Card>

  </div>
</template>

<style scoped>
.mail-settings-container {
  padding: 1.5rem;
}

.field {
  margin-bottom: 1.25rem;
}

.test-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.test-banner.success {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  color: #2e7d32;
}

.test-banner.error {
  background: #ffeaea;
  border: 1px solid #ef9a9a;
  color: #c62828;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
